import { useState, useEffect, useRef, useCallback } from 'react';
import { Edit3, Play, Pause, Loader } from 'lucide-react';
import { ITripCardSlide } from '../../Models/trips/trip-summary.model';
import YouTubePlayer from '../youtube/youtube-player';
import { useVideoPlayerOptional } from '../contexts/video-player-context';
import { AuthenticatedImage } from '../UI/authenticated-image';
import styles from './trip-image-slideshow.module.scss';
import config from '../../config';

/** Resolve a slide imageUrl to a fully-qualified URL */
function resolveSlideUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('/api/')) {
    const apiBase = (
      config.TRAVELHEARTS_API_URL ?? 'http://localhost:8080/api/v1'
    ).replace(/\/api\/v\d.*$/, '');
    const normalised = url.replace(/^\/api\/(?!v\d)/, '/api/v1/');
    return apiBase + normalised;
  }
  return url;
}

interface TripImageSlideshowProps {
  images?: string[];
  altText: string;
  intervalMs?: number;
  pauseOnHover?: boolean;
  slides?: ITripCardSlide[];
  onEditClick?: () => void;
  lazyLoad?: boolean;
}

export function TripImageSlideshow({
  images = [],
  altText,
  intervalMs = 3000,
  pauseOnHover = true,
  slides,
  onEditClick,
  lazyLoad = true
}: TripImageSlideshowProps) {
  const isProxyPhotoUrl = useCallback((url?: string) => {
    return !!url && url.includes('/places/photo');
  }, []);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  // Track which slides should be rendered (start loading)
  const [loadedIndexes, setLoadedIndexes] = useState<Set<number>>(new Set([0]));
  // Track which slides are fully loaded and ready to display
  const [readyIndexes, setReadyIndexes] = useState<Set<number>>(new Set());

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPausedRef = useRef(false);
  const effectiveLengthRef = useRef(0);
  const proxyFailureCountRef = useRef<Map<string, number>>(new Map());
  // Ref to track ready state without causing interval recreation
  const readyIndexesRef = useRef<Set<number>>(new Set());
  const videoPlayerCtx = useVideoPlayerOptional();

  // Keep ref in sync with state
  useEffect(() => {
    readyIndexesRef.current = readyIndexes;
  }, [readyIndexes]);

  // Derive the active list
  const validSlides = slides
    ? slides.filter(
        (s) =>
          s.slideType === 'video_clip' ||
          (!!s.imageUrl && !failedUrls.has(s.imageUrl))
      )
    : null;

  const validImages = slides
    ? []
    : images.filter((url) => !failedUrls.has(url));
  const slideSource = slides ?? images;

  useEffect(() => {
    effectiveLengthRef.current = validSlides
      ? validSlides.length
      : validImages.length;
  });

  const safeIndex =
    effectiveLengthRef.current > 0
      ? currentIndex % effectiveLengthRef.current
      : 0;

  // Preload adjacent slides (next and previous)
  useEffect(() => {
    if (lazyLoad && effectiveLengthRef.current > 1) {
      const nextIndex = (safeIndex + 1) % effectiveLengthRef.current;
      const prevIndex =
        (safeIndex - 1 + effectiveLengthRef.current) %
        effectiveLengthRef.current;

      setLoadedIndexes((loaded) => {
        const updated = new Set(loaded);
        updated.add(safeIndex);
        updated.add(nextIndex);
        updated.add(prevIndex);
        return updated;
      });
    }
  }, [safeIndex, lazyLoad]);

  // Handle pending transitions - wait for image to be ready
  useEffect(() => {
    if (pendingIndex !== null && readyIndexes.has(pendingIndex)) {
      setCurrentIndex(pendingIndex);
      setPendingIndex(null);
    }
  }, [pendingIndex, readyIndexes]);

  // Reset state when source data changes
  useEffect(() => {
    setCurrentIndex(0);
    setPendingIndex(null);
    setFailedUrls(new Set());
    setLoadedIndexes(new Set([0]));
    setReadyIndexes(new Set());
    proxyFailureCountRef.current = new Map();
  }, [slideSource]);

  const handleSlideFetchError = useCallback(
    (originalUrl: string, idx: number) => {
      const isProxy = isProxyPhotoUrl(originalUrl);

      if (isProxy) {
        const currentCount = proxyFailureCountRef.current.get(originalUrl) ?? 0;
        const nextCount = currentCount + 1;
        proxyFailureCountRef.current.set(originalUrl, nextCount);

        // Repeated proxy failures indicate an unstable upstream image.
        // Skip immediately so the card experience remains smooth.
        if (nextCount >= 2) {
          setCurrentIndex((prev) => {
            if (prev !== idx || effectiveLengthRef.current <= 1) return prev;
            return (prev + 1) % effectiveLengthRef.current;
          });
          setPendingIndex((prev) => (prev === idx ? null : prev));
        }
      }

      setFailedUrls((prev) => new Set(prev).add(originalUrl));
    },
    [isProxyPhotoUrl]
  );

  // Interval helpers
  const stopInterval = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startInterval = useCallback(() => {
    stopInterval();
    if (effectiveLengthRef.current <= 1) return;

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => {
        const next = (prev + 1) % effectiveLengthRef.current;

        if (readyIndexesRef.current.has(next)) {
          // Next slide is ready, advance
          return next;
        } else {
          // Not ready, set as pending and stay put
          setPendingIndex(next);
          setLoadedIndexes((loaded) => {
            const updated = new Set(loaded);
            updated.add(next);
            return updated;
          });
          return prev;
        }
      });
    }, intervalMs);
  }, [intervalMs, stopInterval]); // ✅ No currentIndex dependency!

  // Start/restart interval
  useEffect(() => {
    if (isPlaying && !isPausedRef.current) {
      startInterval();
    } else {
      stopInterval();
    }
    return stopInterval;
  }, [slides, images, isPlaying, startInterval, stopInterval]);

  // Hover handlers
  function handleMouseEnter() {
    setIsHovered(true);
    if (pauseOnHover && isPlaying) {
      isPausedRef.current = true;
      stopInterval();
    }
  }

  function handleMouseLeave() {
    setIsHovered(false);
    if (pauseOnHover && isPlaying) {
      isPausedRef.current = false;
      startInterval();
    }
  }

  // Navigate to a specific index
  const navigateToIndex = useCallback((targetIndex: number) => {
    setReadyIndexes((ready) => {
      if (ready.has(targetIndex)) {
        // Image is ready, navigate immediately
        setCurrentIndex(targetIndex);
        setPendingIndex(null);
      } else {
        // Image not ready, set as pending and ensure it's loading
        setPendingIndex(targetIndex);
        setLoadedIndexes((loaded) => {
          const updated = new Set(loaded);
          updated.add(targetIndex);
          return updated;
        });
      }
      return ready;
    });
  }, []);

  // Play/Pause button
  function renderPlayButton() {
    return (
      <button
        type='button'
        className={`${styles.playButton}${isPlaying ? ` ${styles.playButtonPlaying}` : ''}`}
        title={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
        onClick={(e) => {
          e.stopPropagation();
          setIsPlaying((prev) => {
            const next = !prev;
            if (next && lazyLoad) {
              // Pre-load all slides when starting playback
              setLoadedIndexes((loaded) => {
                const total = effectiveLengthRef.current;
                if (loaded.size >= total) return loaded;
                const all = new Set(loaded);
                for (let i = 0; i < total; i++) all.add(i);
                return all;
              });
            }
            return next;
          });
        }}
      >
        {isPlaying ? <Pause size={12} /> : <Play size={12} />}
      </button>
    );
  }

  // Edit button
  function renderEditButton() {
    if (!onEditClick) return null;
    return (
      <button
        type='button'
        className={styles.editButton}
        title='Edit slideshow'
        onClick={(e) => {
          e.stopPropagation();
          onEditClick();
        }}
      >
        <Edit3 size={14} />
      </button>
    );
  }

  // Dots
  function renderDots(count: number) {
    if (count <= 1) return null;
    return (
      <div className={styles.dots}>
        {Array.from({ length: count }).map((_, idx) => (
          <button
            key={idx}
            type='button'
            className={`${styles.dot}${idx === safeIndex ? ` ${styles.dotActive}` : ''}${
              pendingIndex === idx ? ` ${styles.dotPending}` : ''
            }`}
            onClick={(e) => {
              e.stopPropagation();
              navigateToIndex(idx);
            }}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
    );
  }

  // Loading spinner (shown when waiting for pending image)
  function renderLoadingSpinner() {
    if (pendingIndex === null) return null;
    return (
      <div className={styles.loadingOverlay}>
        <Loader
          className={styles.spinner}
          size={32}
        />
      </div>
    );
  }

  // SLIDES MODE
  if (validSlides !== null) {
    if (validSlides.length === 0) return null;

    return (
      <div
        className={styles.slideshow}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {validSlides.map((slide, idx) => {
          const isActive = idx === safeIndex;
          const shouldRender = !lazyLoad || loadedIndexes.has(idx);

          if (slide.slideType === 'video_clip') {
            const thumbnail = resolveSlideUrl(
              slide.thumbnailUrl ?? slide.imageUrl
            );
            const showPlayer = isActive && isHovered && !!slide.videoId;
            return (
              <div
                key={idx}
                className={`${styles.slideWrapper}${isActive ? ` ${styles.active}` : ''}`}
              >
                {thumbnail && (
                  <img
                    src={thumbnail}
                    alt={altText}
                    className={styles.slideThumbnail}
                    style={{
                      opacity: showPlayer ? 0 : 1,
                      transition: 'opacity 0.2s'
                    }}
                    onLoad={() => {
                      setReadyIndexes((ready) => {
                        const updated = new Set(ready);
                        updated.add(idx);
                        return updated;
                      });
                    }}
                  />
                )}
                {showPlayer && (
                  <div
                    className={styles.videoOverlay}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <YouTubePlayer
                      videoId={slide.videoId!}
                      autoplay
                      muted
                      controls={false}
                      startSeconds={slide.videoStartTime ?? 0}
                      endSeconds={slide.videoEndTime}
                      width='100%'
                      height='100%'
                      onReady={(player) =>
                        videoPlayerCtx?.registerPlayer(player)
                      }
                    />
                  </div>
                )}
              </div>
            );
          }

          // image slide
          const resolvedUrl = resolveSlideUrl(slide.imageUrl);
          if (!shouldRender) {
            return (
              <div
                key={idx}
                className={`${styles.slide}${isActive ? ` ${styles.active}` : ''}`}
              />
            );
          }

          return (
            <AuthenticatedImage
              key={idx}
              src={resolvedUrl}
              alt={altText}
              className={`${styles.slide}${isActive ? ` ${styles.active}` : ''}`}
              onLoad={() => {
                setReadyIndexes((ready) => {
                  const updated = new Set(ready);
                  updated.add(idx);
                  return updated;
                });
              }}
              onFetchError={() => handleSlideFetchError(slide.imageUrl!, idx)}
              fallback={<div className={styles.imageLoadingPlaceholder} />}
            />
          );
        })}

        {renderLoadingSpinner()}
        {renderDots(validSlides.length)}
        {renderPlayButton()}
        {renderEditButton()}
      </div>
    );
  }

  // IMAGES MODE (legacy)
  if (validImages.length === 0) return null;

  return (
    <div
      className={styles.slideshow}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {validImages.map((url, idx) => {
        const isActive = idx === safeIndex;
        const shouldRender = !lazyLoad || loadedIndexes.has(idx);

        if (!shouldRender) {
          return (
            <div
              key={url}
              className={`${styles.slide}${isActive ? ` ${styles.active}` : ''}`}
            />
          );
        }

        const resolvedUrl = resolveSlideUrl(url);
        return (
          <AuthenticatedImage
            key={url}
            src={resolvedUrl}
            alt={altText}
            className={`${styles.slide}${isActive ? ` ${styles.active}` : ''}`}
            onLoad={() => {
              setReadyIndexes((ready) => {
                const updated = new Set(ready);
                updated.add(idx);
                return updated;
              });
            }}
            onFetchError={() => handleSlideFetchError(url, idx)}
            fallback={<div className={styles.imageLoadingPlaceholder} />}
          />
        );
      })}

      {renderLoadingSpinner()}
      {renderDots(validImages.length)}
      {renderPlayButton()}
      {renderEditButton()}
    </div>
  );
}
